import type { AnalyticsOverview, AnalyticsRefereeRow, AnalyticsRefereeDetail } from '@proref/types';
import { api, unwrap, type Envelope } from '../lib/api';

// The whole federation-admin dashboard in one call. Every figure is federation-scoped server-side
// (derived from the JWT). `meta.accuracy_target` carries the KPI target line; we return it alongside
// so the page doesn't hard-code it.
export interface AnalyticsOverviewResult {
  overview: AnalyticsOverview;
  accuracyTarget: number;
}

export async function getAnalyticsOverview(): Promise<AnalyticsOverviewResult> {
  const res = await api.get<Envelope<AnalyticsOverview>>('/analytics/overview');
  const overview = unwrap(res.data);
  const meta = res.data.meta as { accuracy_target?: number } | null;
  return { overview, accuracyTarget: meta?.accuracy_target ?? 0.75 };
}

// ─── per-referee (admin only) ────────────────────────────────────────────────

export interface RefereeRosterResult {
  referees: AnalyticsRefereeRow[];
  // The reporting window and the ranking floor travel in meta so the table can label its columns
  // and grey out unranked rows without restating the API's constants.
  windowDays: number;
  minRankedAttempts: number;
}

export async function getRefereeRoster(): Promise<RefereeRosterResult> {
  const res = await api.get<Envelope<AnalyticsRefereeRow[]>>('/analytics/referees');
  const meta = res.data.meta as { window_days?: number; min_ranked_attempts?: number } | null;
  return {
    referees: unwrap(res.data),
    windowDays: meta?.window_days ?? 30,
    minRankedAttempts: meta?.min_ranked_attempts ?? 5,
  };
}

export async function getRefereeDetail(userId: string): Promise<AnalyticsRefereeDetail> {
  const res = await api.get<Envelope<AnalyticsRefereeDetail>>(`/analytics/referees/${userId}`);
  return unwrap(res.data);
}
